'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  X,
  CheckCircle2,
  XCircle,
  Eye,
  Clock,
  Phone,
  User,
  MessageSquare,
  Megaphone,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HistoryEvent {
  id: string;
  timestamp: string;
  type: 'success' | 'failure';
  status: string;
  template_name: string | null;
  campaign_id: string | null;
  message_sid: string | null;
  failure_reason: string | null;
  source: 'chat_messages' | 'drip_message_queue' | 'deleted_leads_log';
}

interface LeadMessageHistoryModalProps {
  phone: string;
  leadName: string | null;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${day} ${month} ${year}, ${String(h12).padStart(2, '0')}:${m} ${ampm}`;
}

function StatusBadge({ event }: { event: HistoryEvent }) {
  if (event.type === 'success') {
    if (event.status === 'read') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-500/10 text-purple-600">
          <Eye size={10} />
          Read
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-500/10 text-green-600">
        <CheckCircle2 size={10} />
        {event.status === 'sent' ? 'Sent' : 'Delivered'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-600">
      <XCircle size={10} />
      Failed
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LeadMessageHistoryModal({
  phone,
  leadName,
  onClose,
}: LeadMessageHistoryModalProps) {
  const supabase = createClient();
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const allEvents: HistoryEvent[] = [];

      // 1. chat_messages — delivered / read
      const { data: chatData } = await supabase
        .from('chat_messages')
        .select('id, created_at, status, template_name, direction')
        .eq('direction', 'out')
        .in('status', ['delivered', 'read', 'sent', 'failed', 'undelivered'])
        .order('created_at', { ascending: false })
        .limit(200);

      // Filter by phone via leads join
      const { data: leadData } = await supabase
        .from('leads')
        .select('id, phone')
        .eq('phone', phone)
        .limit(1);

      const leadId = leadData?.[0]?.id;

      if (leadId) {
        const { data: leadChatData } = await supabase
          .from('chat_messages')
          .select('id, created_at, status, template_name, direction')
          .eq('lead_id', leadId)
          .eq('direction', 'out')
          .order('created_at', { ascending: false })
          .limit(200);

        (leadChatData ?? []).forEach((msg: any) => {
          const isSuccess = ['delivered', 'read', 'sent'].includes(msg.status);
          allEvents.push({
            id: `chat-${msg.id}`,
            timestamp: msg.created_at,
            type: isSuccess ? 'success' : 'failure',
            status: msg.status,
            template_name: msg.template_name ?? null,
            campaign_id: null,
            message_sid: null,
            failure_reason: isSuccess ? null : msg.status,
            source: 'chat_messages',
          });
        });
      }

      // 2. drip_message_queue — sent / failed
      const { data: dripData } = await supabase
        .from('drip_message_queue')
        .select('id, processed_at, scheduled_at, status, campaign_id, message_sid, lead_phone')
        .eq('lead_phone', phone)
        .order('scheduled_at', { ascending: false })
        .limit(200);

      (dripData ?? []).forEach((d: any) => {
        const ts = d.processed_at ?? d.scheduled_at;
        if (!ts) return;
        const isSuccess = d.status === 'sent';
        allEvents.push({
          id: `drip-${d.id}`,
          timestamp: ts,
          type: isSuccess ? 'success' : 'failure',
          status: d.status,
          template_name: null,
          campaign_id: d.campaign_id ?? null,
          message_sid: d.message_sid ?? null,
          failure_reason: isSuccess ? null : (d.status ?? 'unknown'),
          source: 'drip_message_queue',
        });
      });

      // 3. deleted_leads_log — failure records
      const { data: deletedData } = await supabase
        .from('deleted_leads_log')
        .select('id, deleted_at, failure_reason, message_sid, phone')
        .eq('phone', phone)
        .order('deleted_at', { ascending: false })
        .limit(50);

      (deletedData ?? []).forEach((d: any) => {
        allEvents.push({
          id: `del-${d.id}`,
          timestamp: d.deleted_at,
          type: 'failure',
          status: 'failed',
          template_name: null,
          campaign_id: null,
          message_sid: d.message_sid ?? null,
          failure_reason: d.failure_reason ?? 'Unknown',
          source: 'deleted_leads_log',
        });
      });

      // Deduplicate by message_sid if present
      const seen = new Set<string>();
      const deduped = allEvents.filter((e) => {
        if (e.message_sid) {
          if (seen.has(e.message_sid)) return false;
          seen.add(e.message_sid);
        }
        return true;
      });

      // Sort by timestamp descending
      deduped.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setEvents(deduped);
    } catch (err) {
      console.error('[LeadMessageHistory] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [phone, supabase]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const successCount = events.filter((e) => e.type === 'success').length;
  const failureCount = events.filter((e) => e.type === 'failure').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center">
              <User size={16} className="text-blue-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground leading-tight">
                {leadName ?? 'Unknown Lead'}
              </h2>
              <div className="flex items-center gap-1 mt-0.5">
                <Phone size={10} className="text-muted-foreground" />
                <span className="text-[11px] font-mono text-muted-foreground">{phone}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {/* Summary bar */}
        <div className="flex items-center gap-4 px-5 py-3 bg-surface border-b border-border shrink-0">
          <div className="flex items-center gap-1.5">
            <MessageSquare size={13} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-medium">
              {loading ? '—' : events.length} total events
            </span>
          </div>
          <div className="w-px h-3 bg-border" />
          <div className="flex items-center gap-1.5">
            <CheckCircle2 size={12} className="text-green-500" />
            <span className="text-xs text-green-600 font-semibold">{loading ? '—' : successCount} delivered</span>
          </div>
          <div className="w-px h-3 bg-border" />
          <div className="flex items-center gap-1.5">
            <XCircle size={12} className="text-red-500" />
            <span className="text-xs text-red-600 font-semibold">{loading ? '—' : failureCount} failed</span>
          </div>
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="ml-auto p-1 rounded-md text-muted-foreground hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2">
              <RefreshCw size={16} className="animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading history…</span>
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <MessageSquare size={32} className="text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No message history found for this lead</p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />

              <div className="space-y-3">
                {events.map((event, idx) => {
                  const isExpanded = expandedId === event.id;
                  const isFirst = idx === 0;
                  return (
                    <div key={event.id} className="relative flex gap-3">
                      {/* Timeline dot */}
                      <div
                        className={`relative z-10 w-[30px] h-[30px] rounded-full flex items-center justify-center shrink-0 border-2 ${
                          event.type === 'success'
                            ? event.status === 'read' ?'bg-purple-500/10 border-purple-400' :'bg-green-500/10 border-green-400' :'bg-red-500/10 border-red-400'
                        }`}
                      >
                        {event.type === 'success' ? (
                          event.status === 'read' ? (
                            <Eye size={12} className="text-purple-500" />
                          ) : (
                            <CheckCircle2 size={12} className="text-green-500" />
                          )
                        ) : (
                          <XCircle size={12} className="text-red-500" />
                        )}
                      </div>

                      {/* Card */}
                      <div
                        className={`flex-1 bg-surface border rounded-xl overflow-hidden transition-all ${
                          event.type === 'success' ?'border-green-200/60 dark:border-green-900/40' :'border-red-200/60 dark:border-red-900/40'
                        } ${isFirst ? 'ring-1 ring-offset-0 ring-blue-500/20' : ''}`}
                      >
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : event.id)}
                          className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-surface-hover transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <StatusBadge event={event} />
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {mounted ? formatDateTime(event.timestamp) : '—'}
                            </span>
                            {isFirst && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 font-semibold">
                                Latest
                              </span>
                            )}
                          </div>
                          {isExpanded ? (
                            <ChevronUp size={13} className="text-muted-foreground shrink-0" />
                          ) : (
                            <ChevronDown size={13} className="text-muted-foreground shrink-0" />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="px-3 pb-3 pt-0 border-t border-border space-y-1.5">
                            {event.campaign_id && (
                              <div className="flex items-center gap-2">
                                <Megaphone size={11} className="text-muted-foreground shrink-0" />
                                <span className="text-[11px] text-muted-foreground">Campaign:</span>
                                <span className="text-[11px] font-medium text-foreground truncate">{event.campaign_id}</span>
                              </div>
                            )}
                            {event.template_name && (
                              <div className="flex items-center gap-2">
                                <MessageSquare size={11} className="text-muted-foreground shrink-0" />
                                <span className="text-[11px] text-muted-foreground">Template:</span>
                                <span className="text-[11px] font-mono text-foreground">{event.template_name}</span>
                              </div>
                            )}
                            {event.failure_reason && (
                              <div className="flex items-start gap-2">
                                <XCircle size={11} className="text-red-400 shrink-0 mt-0.5" />
                                <span className="text-[11px] text-muted-foreground">Reason:</span>
                                <span className="text-[11px] text-red-600 font-medium">{event.failure_reason}</span>
                              </div>
                            )}
                            {event.message_sid && (
                              <div className="flex items-center gap-2">
                                <Clock size={11} className="text-muted-foreground shrink-0" />
                                <span className="text-[11px] text-muted-foreground">SID:</span>
                                <span className="text-[11px] font-mono text-muted-foreground truncate">{event.message_sid}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover text-muted-foreground font-medium capitalize">
                                {event.source.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
